import React from 'react';
import './ErrorBoundary.css';

/**
 * ErrorBoundary — Catches unhandled React errors and renders a recovery UI.
 * Uses class component as required by React's error boundary API.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });
        console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    }

    handleReload = () => {
        window.location.reload();
    };

    handleResetCache = () => {
        // Clear all alphafocus_ prefixed keys from localStorage
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('alphafocus_')) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="error-boundary">
                    <div className="error-boundary__card">
                        <div className="error-boundary__icon">⚠️</div>
                        <h1 className="error-boundary__title">Something went wrong</h1>
                        <p className="error-boundary__message">
                            An unexpected error occurred. You can try reloading the page, or reset the local cache if the problem persists.
                        </p>
                        {this.state.error && (
                            <details className="error-boundary__details">
                                <summary>Error details</summary>
                                <pre className="error-boundary__stack">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}
                        <div className="error-boundary__actions">
                            <button
                                className="btn btn-ghost btn-lg error-boundary__btn error-boundary__btn--primary"
                                onClick={this.handleReload}
                            >
                                Reload Page
                            </button>
                            <button
                                className="btn btn-ghost btn-lg error-boundary__btn error-boundary__btn--secondary"
                                onClick={this.handleResetCache}
                            >
                                Reset Local Cache
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
